import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import translate from "@insite/client-framework/Translate";
import { PromotionModel } from "@insite/client-framework/Types/ApiModels";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductPartNumbers from "@store/Overrides/Components/ProductPartNumbers";
import { TYPOGRAPHY } from "@store/theme/common/design";
import breakpointValuesMediaQueries from "@store/Framework/Common/Utilities/breakpointValuesMediaQueries";
import React from "react";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    promotions?: PromotionModel[];
    showSavingsAmount: boolean;
    showSavingsPercent: boolean;
    editable: boolean;
    extendedStyles?: CartLineCardExpandedStyles;
}

type Props = OwnProps & HasCartLineContext;

export interface CartLineCardExpandedStyles {
    price?: ProductPriceStyles;
    container?: GridContainerProps;
    productImageGridItem?: GridItemProps;
    productImage?: ProductImageStyles;
    orderLineInfoGridItem?: GridItemProps;
    productBrand?: ProductBrandStyles;
    productBrandAndDescriptionGridItem?: GridItemProps;
    productInfoPartNumbersGridItem?: GridItemProps;
    productDescription?: ProductDescriptionStyles;
    configurationOptionText?: TypographyProps;
    priceHeadingAndText?: SmallHeadingAndTextStyles;
    promotionNameText?: TypographyProps;
    quantityGridItem?: GridItemProps;
    extendedUnitNetPriceGridItem?: GridItemProps;
    extendedUnitNetPrice?: SmallHeadingAndTextStyles;
    configSectionWrapper?: InjectableCss;
    configSectionLabel?: TypographyProps;
    productInfosContainer?: GridContainerProps;
    productInfosInnerContainer?: GridContainerProps;
    orderLinePriceGridItem?: GridItemProps;
    productInfoGridItem?: GridItemProps;
    erpNumberHeadingAndText?: SmallHeadingAndTextStyles;
    manufacturerHeadingAndText?: SmallHeadingAndTextStyles;
}

const productInfoGridStyles: GridItemProps = {
    css: css`
        flex-direction: column;
        text-align: center;
        ${({ theme }) =>
            breakpointValuesMediaQueries(
                theme,
                [{ values: ["10px !important", null, "0 !important", null, null], properties: ["margin-bottom"] }],
                mediaOptions.min,
            )}
    `,
    width: 4,
};

export const cartLineCardExpandedStyles: CartLineCardExpandedStyles = {
    container: {
        gap: 0,
        css: css`
            border-top: 1px solid ${getColor("common.border")};
            padding: 1rem 0;

            > ${GridOffset} {
                justify-content: space-between;
                align-items: center;
            }
        `,
    },
    productInfosInnerContainer: {
        gap: 0,
    },
    productInfosContainer: {
        gap: 0,
        css: css`
            padding-top: 15px;
        `,
    },
    productBrandAndDescriptionGridItem: {
        css: css`
            flex-direction: column;
            padding-top: 0;
            padding-bottom: 0;
        `,
        width: 12,
    },
    productInfoPartNumbersGridItem: {
        width: 12,
        css: css`
            padding-top: 5px;
        `,
    },
    productInfoGridItem: {
        width: [10, 10, 10, 5, 5],
        css: css`
            padding-left: 20px;

            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [{ values: ["20px", null, null, "0", null], properties: ["padding-bottom"] }],
                    mediaOptions.min,
                )}
        `,
    },
    productImageGridItem: {
        width: [2, 2, 2, 1, 1],
        printWidth: 1,
        css: css`
            @media print {
                max-height: 83px;
                max-width: 83px;
                padding: 0;
            }

            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [{ values: ["20px", null, null, "0", null], properties: ["padding-bottom"] }],
                    mediaOptions.min,
                )}
        `,
    },
    productImage: {
        image: {
            css: css`
                img {
                    height: 100%;
                    max-width: 100px;
                }
            `,
        },
    },
    orderLineInfoGridItem: {
        width: [12, 12, 12, 6, 6],
        printWidth: 11,
    },

    productBrand: {
        nameText: {
            color: "text.main",
        },
    },
    orderLinePriceGridItem: {
        ...productInfoGridStyles,
    },
    quantityGridItem: {
        ...productInfoGridStyles,
    },
    extendedUnitNetPriceGridItem: {
        ...productInfoGridStyles,
    },
    priceHeadingAndText: {
        heading: {
            css: css`
                text-transform: inherit;
                color: ${getColor("text.accent")};

                ${({ theme }) =>
                    breakpointValuesMediaQueries(
                        theme,
                        [{ values: ["block", null, null, "none", null], properties: ["display"] }],
                        mediaOptions.min,
                    )}
            `,
        },
        text: {
            weight: TYPOGRAPHY.weight.bold,
            css: css`
                color: ${getColor("text.dark")};
            `,
        },
    },
    price: {
        price: {
            priceText: {
                weight: TYPOGRAPHY.weight.medium,
                css: css`
                    color: ${getColor("text.dark")};
                `,
            },
        },
    },
    productDescription: {
        productDetailLink: {
            css: css`
                width: 100%;
            `,
            typographyProps: {
                css: css`
                    width: 100%;
                    font-size: 18px;
                    color: ${getColor("text.dark")} !important;
                    ${wrapInContainerStyles}
                `,
            },
        },
    },
    extendedUnitNetPrice: {
        text: {
            weight: TYPOGRAPHY.weight.bold,
            size: TYPOGRAPHY.size.base,
            css: css`
                color: ${getColor("text.dark")};
            `,
        },
    },
    configSectionLabel: {
        weight: TYPOGRAPHY.weight.bold,
        css: css`
            display: block;
            color: ${getColor("text.dark")};
        `,
    },
    configurationOptionText: {
        css: css`
            display: block;
        `,
    },
};

const CartLineCardExpanded: React.FC<Props> = ({
    cartLine,
    showSavingsAmount,
    showSavingsPercent,
    promotions,
    extendedStyles,
}) => {
    const [styles] = React.useState(() => mergeToNew(cartLineCardExpandedStyles, extendedStyles));

    return (
        <GridContainer
            {...styles.container}
            data-test-selector={`cartline_expanded_${cartLine.productId}_${cartLine.unitOfMeasure}`}
        >
            <GridItem {...styles.productImageGridItem}>
                <ProductImage product={cartLine} extendedStyles={styles.productImage} />
            </GridItem>
            <GridItem {...styles.productInfoGridItem}>
                <GridContainer {...styles.productInfosContainer}>
                    <GridItem {...styles.productBrandAndDescriptionGridItem}>
                        {cartLine.brand && <ProductBrand brand={cartLine.brand} extendedStyles={styles.productBrand} />}
                        <ProductDescription product={cartLine} extendedStyles={styles.productDescription} />
                        {cartLine.sectionOptions!.length > 0 &&
                            cartLine.sectionOptions!.map(option => (
                                <StyledWrapper {...styles.configSectionWrapper} key={option.sectionOptionId}>
                                    <Typography {...styles.configSectionLabel}>
                                        {translate(option.sectionName)}
                                    </Typography>
                                    <Typography {...styles.configurationOptionText}>{option.optionName}</Typography>
                                </StyledWrapper>
                            ))}
                    </GridItem>
                    <GridItem {...styles.productInfoPartNumbersGridItem}>
                        <ProductPartNumbers productNumber={cartLine.erpNumber} />
                    </GridItem>
                </GridContainer>
            </GridItem>
            <GridItem {...styles.orderLineInfoGridItem}>
                <GridContainer {...styles.productInfosInnerContainer}>
                    <GridItem {...styles.orderLinePriceGridItem}>
                        <SmallHeadingAndText
                            heading={translate("Price")}
                            text=""
                            extendedStyles={styles.priceHeadingAndText}
                        />
                        <ProductPrice
                            product={cartLine}
                            showSavings={true}
                            showLabel={false}
                            showSavingsAmount={showSavingsAmount}
                            showSavingsPercent={showSavingsPercent}
                            extendedStyles={styles.price}
                        />
                        {promotions?.map(promotion => (
                            <Typography {...styles.promotionNameText} key={promotion.id}>
                                {promotion.name}
                            </Typography>
                        ))}
                    </GridItem>
                    <GridItem {...styles.quantityGridItem}>
                        <SmallHeadingAndText
                            heading={translate("Qty")}
                            text={cartLine.qtyOrdered!}
                            extendedStyles={styles.priceHeadingAndText}
                        />
                    </GridItem>
                    <GridItem {...styles.extendedUnitNetPriceGridItem}>
                        {cartLine.pricing && (
                            <SmallHeadingAndText
                                heading={translate("Subtotal")}
                                text={cartLine.pricing.extendedUnitNetPriceDisplay}
                                extendedStyles={styles.priceHeadingAndText}
                            />
                        )}
                    </GridItem>
                </GridContainer>
            </GridItem>
        </GridContainer>
    );
};

export default withCartLine(CartLineCardExpanded);
